import React from 'react';
import { useQuery } from '@tanstack/react-query';
import portfolioService, { RiskData } from '../../services/portfolio';

interface Props {
  portfolioId: number;
}

// Mirrors the ETF Similarity heatmap palette: white → navy as correlation rises.
// Correlation is clamped to [0, 1] for shading (negative pairs read as ~white).
function interpolateColor(value: number): { background: string; color: string } {
  const v = Math.max(0, Math.min(1, value));
  const r = Math.round(255 * (1 - v));
  const g = Math.round(255 - 250 * v);
  const b = Math.round(255 - 200 * v);
  return { background: `rgb(${r},${g},${b})`, color: v > 0.5 ? '#ffffff' : '#18181b' };
}

const ReturnCorrelationCard: React.FC<Props> = ({ portfolioId }) => {
  const { data } = useQuery<RiskData>({
    queryKey: ['portfolio-risk', portfolioId],
    queryFn: () => portfolioService.getPortfolioRisk(portfolioId),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const charts = data?.charts;
  const labels = charts?.correlation_labels ?? [];
  const matrix = charts?.correlation_matrix ?? [];
  if (labels.length < 2 || matrix.length < 2) return null;

  // Axis labels stay as tickers/identifiers; hover reveals the full name.
  const nameByLabel = new Map((charts?.instruments ?? []).map(i => [i.identifier, i.name]));
  const fullName = (label: string) => nameByLabel.get(label) || label;

  const n = labels.length;
  const labelWidth = 96;                              // row-label column — a touch wider
  const cellH = n <= 6 ? 48 : n <= 10 ? 42 : 34;      // fixed height; widths stretch to fill
  const fontSize = n <= 10 ? '0.72rem' : '0.62rem';

  return (
    <div className="card card-forest h-100">
      <div className="card-body p-3 p-md-4 d-flex flex-column">
        <div className="mb-3">
          <h5 className="card-title mb-0 fw-semibold text-forest-600" style={{ fontSize: '0.95rem' }}>
            Return Correlation
          </h5>
          <div style={{ fontSize: '0.75rem', color: '#a1a1aa', marginTop: '2px' }}>
            Daily-return co-movement across held instruments ({charts!.window_years}Y)
          </div>
        </div>

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'separate', borderSpacing: '3px' }}>
            <thead>
              <tr>
                <th style={{ width: labelWidth }} />
                {labels.map(l => (
                  <th key={l} title={fullName(l)} style={{
                    fontSize, fontWeight: 600, color: '#52525b',
                    textAlign: 'center', paddingBottom: '6px', verticalAlign: 'bottom',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {l}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {labels.map((rowLabel, i) => (
                <tr key={rowLabel}>
                  <td title={fullName(rowLabel)} style={{
                    width: labelWidth, fontSize, fontWeight: 600, color: '#52525b',
                    textAlign: 'right', paddingRight: '10px',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>
                    {rowLabel}
                  </td>
                  {labels.map((colLabel, j) => {
                    const val = matrix[i]?.[j] ?? 0;
                    const isDiag = i === j;
                    const cellStyle = isDiag
                      ? { background: '#f4f4f5', color: '#a1a1aa' }
                      : interpolateColor(val);
                    return (
                      <td key={colLabel}
                        title={isDiag ? fullName(rowLabel) : `${fullName(rowLabel)} ↔ ${fullName(colLabel)}: ${val.toFixed(2)}`}
                        style={{
                          height: cellH, borderRadius: '6px',
                          textAlign: 'center', verticalAlign: 'middle',
                          fontSize, fontWeight: isDiag ? 400 : 600,
                          fontVariantNumeric: 'tabular-nums',
                          ...cellStyle,
                        }}>
                        {isDiag ? '—' : val.toFixed(2)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ReturnCorrelationCard;
